import {
  Bot,
  Check,
  Crown,
  DoorOpen,
  KeyRound,
  Shield,
  Swords,
  UserRound,
  UserX,
  WandSparkles,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { nextAutomaticSlideDeadline } from "../automatic";
import { type BoardCanvasHandle, BoardCanvas } from "../BoardCanvas";
import { type EffectSettings } from "../effects";
import { type GrowthProfile, type GrowthSkill, type RoomView } from "../types";
import { readAutoModePreference, writeAutoModePreference } from "../ui/autoPreference";
import { EquipmentDropNotifier, IncomingHitFeedback } from "../ui/CombatFeedback";
import { AttackElementMark, TargetElementMark } from "../ui/Elements";
import { RoomEnemyInspector } from "../ui/EnemyDetails";
import { formatDisplayNumber } from "../ui/format";
import { spriteForRaidBoss } from "../ui/gameArt";
import { Meter, Status } from "../ui/Resources";
import {
  SkillButton,
  skillCooldownState,
  SkillIcon,
  skillResourceLimitText,
} from "../ui/SkillControls";
import { findAutomaticSlide } from "../wasm";

export const RAID_EVENT_LABELS: Record<string, string> = {
  crystal_shield: "结晶护盾",
  collapse: "崩塌",
  drain_ritual: "汲取仪式",
  vein_mark: "岩脉标记",
  key_lock: "钥锁",
};

export function MultiplayerRoomScreen({
  view,
  clockNow,
  growth,
  simReady,
  colorBlind,
  effectSettings,
  busy,
  onReady,
  onStart,
  onUpdate,
  onKick,
  onTransfer,
  onLeave,
  onSlide,
  onSkill,
  onRetreat,
}: {
  view: RoomView;
  clockNow: () => number;
  growth: GrowthProfile;
  simReady: boolean;
  colorBlind: boolean;
  effectSettings: EffectSettings;
  busy: boolean;
  onReady: (ready: boolean) => Promise<void>;
  onStart: (solo: boolean) => Promise<void>;
  onLeave: () => Promise<void>;
  onUpdate: (
    mode: "raid" | "duo",
    visibility: "public" | "guild" | "friends" | "invite",
  ) => Promise<void>;
  onKick: (userId: string) => Promise<void>;
  onTransfer: (userId: string) => Promise<void>;
  onSlide: (
    axis: "row" | "col",
    index: number,
    distance: number,
    hash: string,
    automated: boolean,
  ) => Promise<void>;
  onSkill: (skill: string, target: string) => Promise<void>;
  onRetreat: (confirm: boolean) => Promise<void>;
}) {
  const room = view.room;
  const me = room.participants.find((participant) => participant.userId === view.you);
  const [supportTarget, setSupportTarget] = useState(view.you);
  const [clock, setClock] = useState(clockNow);
  const [autoMode, setAutoMode] = useState(readAutoModePreference);
  const autoCursor = useRef(0);
  const autoSubmitting = useRef(false);
  const autoNextSlideAt = useRef(0);
  const boardCanvasRef = useRef<BoardCanvasHandle>(null);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(clockNow()), 100);
    return () => window.clearInterval(timer);
  }, [clockNow]);
  useEffect(() => {
    setAutoMode(readAutoModePreference());
    autoCursor.current = 0;
    autoSubmitting.current = false;
    autoNextSlideAt.current = 0;
  }, [room.id, room.status]);
  const automaticSlideIntervalMs = Math.max(1, me?.automaticSlideIntervalMs || 1000);
  const automaticRuntime = useRef({ busy, me, onSlide, roomStatus: room.status, simReady });
  automaticRuntime.current = { busy, me, onSlide, roomStatus: room.status, simReady };
  useEffect(() => {
    if (!autoMode) {
      autoNextSlideAt.current = 0;
      return;
    }
    autoNextSlideAt.current = nextAutomaticSlideDeadline(
      autoNextSlideAt.current,
      clockNow(),
      automaticSlideIntervalMs,
      automaticRuntime.current.me?.lastAutomaticSlideAt,
    );
    const timer = window.setInterval(
      () => {
        const current = automaticRuntime.current;
        const participant = current.me;
        const now = clockNow();
        autoNextSlideAt.current = nextAutomaticSlideDeadline(
          autoNextSlideAt.current,
          now,
          automaticSlideIntervalMs,
          participant?.lastAutomaticSlideAt,
        );
        if (
          now < autoNextSlideAt.current ||
          !participant ||
          current.roomStatus !== "active" ||
          autoSubmitting.current ||
          current.busy ||
          !current.simReady ||
          participant.hp <= 0 ||
          now < participant.lockedUntil
        )
          return;
        const slide = findAutomaticSlide(
          participant.board,
          {
            wrapMatches: participant.mechanics?.wrapMatches ?? false,
            woodOreUnion: participant.mechanics?.woodOreUnion ?? false,
          },
          autoCursor.current,
        );
        autoNextSlideAt.current = now + automaticSlideIntervalMs;
        if (!slide) return;
        autoCursor.current = slide.nextCursor;
        autoSubmitting.current = true;
        const submission = boardCanvasRef.current
          ? boardCanvasRef.current.playAutomatedSlide(
              slide.axis,
              slide.index,
              slide.distance,
              participant.boardHash,
            )
          : current
              .onSlide(slide.axis, slide.index, slide.distance, participant.boardHash, true)
              .then(() => true);
        void submission.finally(() => {
          autoSubmitting.current = false;
        });
      },
      Math.min(100, automaticSlideIntervalMs),
    );
    return () => window.clearInterval(timer);
  }, [autoMode, automaticSlideIntervalMs, clockNow]);
  if (!me)
    return (
      <main className="multiplayer-screen">
        <p>房间成员状态已失效。</p>
        <button className="secondary-command" onClick={() => void onLeave()}>
          返回
        </button>
      </main>
    );
  const skills = (me.skillLoadout ?? [])
    .map((id) => growth.skills.find((skill) => skill.id === id))
    .filter((skill): skill is GrowthSkill => Boolean(skill));
  const active = room.status === "active";
  const owner = room.ownerUserId === view.you;
  const disabled = busy || !active || me.hp <= 0 || clock < me.lockedUntil;
  const confirmRetreat = Boolean(room.retreatRequestedBy && room.retreatRequestedBy !== view.you);
  const retreatAvailableAt = room.retreatAvailableAt ?? (room.startedAt ?? clock) + 90_000;
  const retreatRemainingSec = active
    ? Math.max(0, Math.ceil((retreatAvailableAt - clock) / 1000))
    : 0;
  const retreatLocked = active && retreatRemainingSec > 0;
  const nextBossAttackIn = room.boss
    ? Math.max(
        0,
        Math.ceil((Math.max(room.boss.nextAttackAt, room.boss.stunnedUntil) - clock) / 1000),
      )
    : 0;
  const incomingHitsFor = (participant: typeof me) =>
    participant.lastIncomingHits ??
    (participant.lastHitAt
      ? [
          {
            sourceId: room.mode === "raid" ? "raid_boss" : "duo_monster",
            damage: participant.lastDamageTaken ?? 0,
            blocked: participant.lastDamageBlocked ?? 0,
            at: participant.lastHitAt,
          },
        ]
      : []);
  const recentRoomHits = room.participants.flatMap((participant) =>
    incomingHitsFor(participant).filter((hit) => Math.abs(view.serverNow - hit.at) < 900),
  );
  const roomDamageEvents = room.participants.filter(
    (participant) =>
      participant.lastDamage &&
      participant.lastDamageAt &&
      Math.abs(view.serverNow - participant.lastDamageAt) < 900,
  );
  const bossUnderAttack = roomDamageEvents.length > 0;
  const bossAttacking = recentRoomHits.length > 0;
  return (
    <main className="multiplayer-screen">
      <EquipmentDropNotifier drops={me.equipmentDrops ?? []} scopeKey={`${room.id}:${view.you}`} />
      <header className="multiplayer-header">
        <div>
          <span className="eyebrow">
            {room.mode === "raid" ? "周常 Raid" : "协作矿道"} · {room.participants.length}/
            {room.maxPlayers}
          </span>
          <h1>{room.status === "lobby" ? "狩猎大厅" : room.message || "协同战斗"}</h1>
        </div>
        <button
          className="icon-button"
          disabled={busy || retreatLocked}
          onClick={() => void (active ? onRetreat(confirmRetreat) : onLeave())}
          aria-label={
            active ? (retreatLocked ? `${retreatRemainingSec} 秒后可撤退` : "撤退") : "离开房间"
          }
          title={
            active ? (retreatLocked ? `${retreatRemainingSec} 秒后可撤退` : "撤退") : "离开房间"
          }
        >
          <DoorOpen size={18} />
          {retreatLocked && <span className="retreat-countdown">{retreatRemainingSec}</span>}
        </button>
      </header>
      <section className="room-members">
        {room.participants.map((participant) => {
          const hits = incomingHitsFor(participant);
          const freshHits = hits.filter((hit) => Math.abs(view.serverNow - hit.at) < 900);
          return (
            <div
              className={`room-member${participant.userId === view.you ? " is-you" : ""}${freshHits.some((hit) => hit.damage > 0) ? " is-damaged" : ""}${freshHits.some((hit) => hit.blocked > 0) ? " is-blocking" : ""}`}
              key={participant.userId}
            >
              <span className="member-avatar">
                <UserRound size={17} />
              </span>
              <div>
                <strong>
                  {participant.nickname}
                  {participant.userId === room.ownerUserId ? " · 房主" : ""}
                </strong>
                <small>
                  {active
                    ? `${participant.hp} / ${participant.maxHp}`
                    : participant.connected
                      ? participant.ready
                        ? "已准备"
                        : "等待准备"
                      : "托管中"}
                </small>
              </div>
              {!active && owner && participant.userId !== view.you && (
                <span className="room-member-tools">
                  <button
                    className="icon-button"
                    disabled={busy}
                    onClick={() => void onTransfer(participant.userId)}
                    title="转让房主"
                    aria-label={`转让房主给${participant.nickname}`}
                  >
                    <Crown size={14} />
                  </button>
                  <button
                    className="icon-button"
                    disabled={busy}
                    onClick={() => void onKick(participant.userId)}
                    title="移出房间"
                    aria-label={`移出${participant.nickname}`}
                  >
                    <UserX size={14} />
                  </button>
                </span>
              )}
              {active && (
                <>
                  <Meter
                    value={(participant.hp / Math.max(1, participant.maxHp)) * 100}
                    tone="hp"
                  />
                  <IncomingHitFeedback hits={hits} now={view.serverNow} compact />
                </>
              )}
            </div>
          );
        })}
      </section>
      {room.status === "lobby" && (
        <section className="room-lobby">
          <div className="invite-code">
            <span>邀请码</span>
            <strong>{room.inviteCode}</strong>
          </div>
          {owner && (
            <div className="room-config">
              <label>
                <span>任务</span>
                <select
                  value={room.mode}
                  disabled={busy}
                  onChange={(event) =>
                    void onUpdate(event.currentTarget.value as "raid" | "duo", room.visibility)
                  }
                >
                  <option value="raid">周常 Raid</option>
                  <option value="duo">协作矿道</option>
                </select>
              </label>
              <label>
                <span>可见性</span>
                <select
                  value={room.visibility || (room.public ? "public" : "invite")}
                  disabled={busy}
                  onChange={(event) =>
                    void onUpdate(
                      room.mode,
                      event.currentTarget.value as "public" | "guild" | "friends" | "invite",
                    )
                  }
                >
                  <option value="public">公开</option>
                  <option value="guild">仅公会</option>
                  <option value="friends">仅好友</option>
                  <option value="invite">仅邀请</option>
                </select>
              </label>
            </div>
          )}
          <div className="room-lobby-actions">
            <button
              className="secondary-command compact"
              disabled={busy}
              onClick={() => void onReady(!me.ready)}
            >
              <Check size={16} />
              {me.ready ? "取消准备" : "准备"}
            </button>
            {owner && (
              <button
                className="primary-command compact"
                disabled={busy || room.participants.some((participant) => !participant.ready)}
                onClick={() => void onStart(room.participants.length === 1)}
              >
                <Swords size={16} />
                开始任务
              </button>
            )}
          </div>
        </section>
      )}
      {active && room.mode === "raid" && room.boss && (
        <section
          className={`room-boss has-sprite${bossAttacking ? " is-attacking" : ""}${bossUnderAttack ? " is-hit" : ""}`}
          data-effect-target="enemy"
        >
          <RoomEnemyInspector room={room} />
          <img
            className="room-boss-sprite"
            src={spriteForRaidBoss(room.boss.id)}
            alt=""
            aria-hidden="true"
          />
          <div>
            <span className="room-target-name" title={room.boss.description}>
              {room.boss.name}
              <TargetElementMark
                resistance={room.boss.resistanceElement}
                weakness={room.boss.weaknessElement}
              />
              <em>· 阶段 {room.boss.phase}</em>
            </span>
            <strong>
              {room.boss.hp} / {room.boss.maxHp}
            </strong>
          </div>
          <Meter value={(room.boss.hp / Math.max(1, room.boss.maxHp)) * 100} tone="enemy" />
          <div className="room-boss-meta">
            <span className="mutation-chip" title={room.mutation.description}>
              {room.mutation.name}
            </span>
            <small className="room-attack-timer">
              <Zap size={12} />
              {room.boss.stunnedUntil > clock ? "硬直" : "攻击"} {nextBossAttackIn}s
            </small>
            {room.raidEvent && (
              <small>
                {RAID_EVENT_LABELS[room.raidEvent.id] ?? "协同事件"} ·{" "}
                {Math.max(0, Math.ceil((room.raidEvent.endsAt - clock) / 1000))}s
              </small>
            )}
          </div>
          {roomDamageEvents.map((participant, index) => (
            <div
              className={`room-damage-feedback is-${participant.lastElementOutcome ?? "neutral"}`}
              key={`${participant.userId}-${participant.lastDamageAt}`}
              style={{ "--damage-offset": `${index * 34}px` } as React.CSSProperties}
            >
              -{participant.lastDamage}
              <small>{participant.nickname}</small>
            </div>
          ))}
        </section>
      )}
      {active && room.mode === "duo" && (
        <section
          className={`room-boss${bossAttacking ? " is-attacking" : ""}${bossUnderAttack ? " is-hit" : ""}`}
          data-effect-target="enemy"
        >
          <RoomEnemyInspector room={room} />
          <div>
            <span className="room-target-name">
              矿道敌群
              <TargetElementMark
                resistance={room.duoResistanceElement ?? "neutral"}
                weakness={room.duoWeaknessElement ?? "neutral"}
              />
              <em>· 第 {room.duoDepth} 层</em>
            </span>
            <strong>
              {room.duoMonsterHp} / {room.duoMonsterMaxHp}
            </strong>
          </div>
          <Meter
            value={((room.duoMonsterHp ?? 0) / Math.max(1, room.duoMonsterMaxHp ?? 1)) * 100}
            tone="enemy"
          />
          {roomDamageEvents.map((participant, index) => (
            <div
              className={`room-damage-feedback is-${participant.lastElementOutcome ?? "neutral"}`}
              key={`${participant.userId}-${participant.lastDamageAt}`}
              style={{ "--damage-offset": `${index * 34}px` } as React.CSSProperties}
            >
              -{participant.lastDamage}
              <small>{participant.nickname}</small>
            </div>
          ))}
        </section>
      )}
      {active && (
        <>
          <section className="room-combat-card">
            <div className="room-weapon-element">
              <AttackElementMark element={me.weaponElement ?? "neutral"} />
              <label
                className={`auto-mode-toggle${autoMode ? " is-active" : ""}`}
                title={`每 ${formatDisplayNumber(automaticSlideIntervalMs / 1000, true)} 秒自动消除`}
              >
                <Bot size={16} aria-hidden="true" />
                <span>自动</span>
                <input
                  type="checkbox"
                  checked={autoMode}
                  disabled={!simReady || me.hp <= 0}
                  onChange={(event) => {
                    const enabled = event.currentTarget.checked;
                    writeAutoModePreference(enabled);
                    setAutoMode(enabled);
                  }}
                />
                <i aria-hidden="true" />
              </label>
              {me.lastElementOutcome === "weakness" && <strong>克制 +50%</strong>}
              {me.lastElementOutcome === "resisted" && <strong>抵抗 -25%</strong>}
            </div>
            <div className="status-strip">
              <Status
                icon={<Shield size={16} />}
                label="格挡"
                value={me.block}
                effectTarget="block"
              />
              <Status
                icon={<WandSparkles size={16} />}
                label="法力"
                value={`${me.mana}/${me.manaCap}`}
                effectTarget="mana"
              />
              <Status
                icon={<KeyRound size={16} />}
                label="钥匙"
                value={me.keys}
                effectTarget="key"
              />
            </div>
            {room.participants.length > 1 && (
              <label className="support-target">
                <span>支援目标</span>
                <select
                  value={supportTarget}
                  onChange={(event) => setSupportTarget(event.target.value)}
                >
                  {room.participants.map((participant) => (
                    <option key={participant.userId} value={participant.userId}>
                      {participant.userId === view.you ? "自己" : participant.nickname}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="room-skill-bar">
              {skills.map((skill) => {
                const cooldown = skillCooldownState(
                  me.skillCooldowns,
                  me.skillCooldownDurations,
                  skill.id,
                  clock,
                  skill.cooldownMs,
                );
                return (
                  <SkillButton
                    key={skill.id}
                    icon={<SkillIcon skillId={skill.id} />}
                    name={skill.name}
                    effectTarget={skill.target}
                    effectKind={
                      skill.target === "self" || skill.target === "all_allies"
                        ? "support"
                        : skill.branch === "warrior"
                          ? "strike"
                          : "magic"
                    }
                    effectElement={
                      skill.damageElement === "weapon" ? "neutral" : skill.damageElement
                    }
                    description={`${skill.description}${skillResourceLimitText(skill, me.skillResourceUseLimits?.[skill.id] ?? skill.resourceUseLimit)} · R${me.skillRanks?.[skill.id] ?? skill.rank}`}
                    cost={me.freeCastReady ? 0 : (me.skillManaCosts?.[skill.id] ?? skill.manaCost)}
                    cooldownRemainingMs={cooldown.remainingMs}
                    cooldownDurationMs={cooldown.durationMs}
                    disabled={
                      disabled ||
                      cooldown.remainingMs > 0 ||
                      (!me.freeCastReady &&
                        me.mana < (me.skillManaCosts?.[skill.id] ?? skill.manaCost)) ||
                      (skill.id === "free_cast" && !!me.freeCastReady)
                    }
                    onClick={() => void onSkill(skill.id, supportTarget)}
                  />
                );
              })}
            </div>
          </section>
          <BoardCanvas
            ref={boardCanvasRef}
            board={me.board}
            boardHash={me.boardHash}
            revision={room.revision}
            cascades={me.lastCascades ?? []}
            transformations={me.lastTransformations ?? []}
            disabled={disabled || autoMode}
            queued={false}
            colorBlind={colorBlind}
            effectSettings={effectSettings}
            fallDelayMs={me.mechanics?.fallDelayMs ?? 0}
            lockedRow={-1}
            foggedCells={[]}
            wrapMatches={me.mechanics?.wrapMatches ?? false}
            woodOreUnion={me.mechanics?.woodOreUnion ?? false}
            onSlide={(axis, index, distance, automated = false) =>
              onSlide(axis, index, distance, me.boardHash, automated)
            }
          />
          <button
            className="secondary-command compact room-retreat"
            disabled={busy || retreatLocked}
            onClick={() => void onRetreat(confirmRetreat)}
          >
            <DoorOpen size={16} />
            {retreatLocked
              ? `${retreatRemainingSec}秒后可撤退`
              : confirmRetreat
                ? "确认撤退"
                : "发起撤退"}
          </button>
        </>
      )}
    </main>
  );
}
